import axios, { AxiosError } from 'axios';
import {
  API_REQUEST_TIME_OUT,
  NETWORK_ERROR,
  TIME_OUT_EXCEED,
} from "../appConstants/AppConstant";
import type {
  RootApiResponseExtends,
  IBaseUrl,
  RootApiResponseWithData,
} from '../interfaces/BaseApiResponse';
//import { useTokenStore } from '../storage/tokenState';
import { refreshTokenDuplicate } from '../services/AuthService';
import { REFRESH_TOKEN } from '../appConstants/APIEndpoint';
//import { appPackageName, HOSTNAME, version } from '@env';
import { isError, pick } from 'lodash';
import { getDeviceId } from '../api/commonApi';
import { LANGUAGE } from '../constant';

const deviceID = getDeviceId();

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_HOST_NAME}`,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    version: `${process.env.REACT_APP_VERSION}`,
    appversion: `${process.env.REACT_APP_VERSION}`,
    Platform: 'android',
    appname: `${process.env.REACT_APP_APP_NAME || 'pwa.verticalbyzupee.com'}`,
    language: localStorage.getItem("language") || LANGUAGE,
    'Package-Name': `${process.env.REACT_APP_APPPACKAGENAME || 'pwa.verticalbyzupee.com'}`,
    isPwa: true,
    deviceID,
    // 'x-zupee-env': 'cse',
    'x-zupee-source': 'web',
    // "z-stage-env-id":"stv-prya80a3bac",
    ...(sessionStorage.getItem("ppEnabled") && {
    'x-zupee-pp-token': sessionStorage.getItem("googleToken") || "",
  })
  },
  timeout: API_REQUEST_TIME_OUT,
});

export const setAuthTokenInHeader = (
  accessToken: string,
  identity?: string,
) => {
  axiosInstance.defaults.headers.authorization = accessToken;
  const language = localStorage.getItem("language") || LANGUAGE;  
  if (language) {
    axiosInstance.defaults.headers.language = language;
  }
  if (identity) {
    axiosInstance.defaults.headers.ludoUserId = identity;
  }
};

export const updateLanguageInHeader = () => {
  const language = localStorage.getItem("language") || LANGUAGE;
  if (language) {
    axiosInstance.defaults.headers.language = language;
  }
};

export function getAuthTokenHeader() {
  return pick(axiosInstance.defaults.headers, [
    'Platform',
    'appname',
    'version',
    'appversion',
    'language',
    'Package-Name',
    'appnameios',
    'authorization',
    'ludoUserId',
  ]);
}

export const getBaseUrl = () => axiosInstance.defaults.baseURL;


axiosInstance.interceptors.request.use(
  config => {
    // Track API call - this prevents inactivity timer from resetting
    const { trackApiCall } = require('../hooks/useAutoLogout');
    trackApiCall();
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const isRefreshUrl = error?.config?.url?.includes(REFRESH_TOKEN);
    if (error.response && error.response.status === 401 && !isRefreshUrl) {
      try {
        await refreshTokenDuplicate();
        const updatedConfig = { ...error?.config };
        return axiosInstance.request(updatedConfig);
      } catch (refreshError) {
        // If refresh token also fails, logout immediately
        const { handle401Error } = require('../hooks/useAutoLogout');
        handle401Error();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export async function refreshTokenAPI(urlConfig: IBaseUrl,
  requestData = {},
  requestHeaders = {}) {

  try {
    const res = await axiosInstance.post(
      urlConfig.url,
      requestData,
      requestHeaders,
    );
    const responseData = res?.data;

    return responseData;
  } catch (error: any) {
    logApiError(error, 'refreshTokenAPI', urlConfig);
    return { en: urlConfig.en, response: { success: false } };
  }
}


export async function requestApiGet<T>(
  urlConfig: IBaseUrl,
  config = {},
): Promise<RootApiResponseWithData<T>> {

  try {
    const res = await axiosInstance.get(urlConfig.url, config);
    const responseData = res?.data;
   
    return responseData;
  } catch (error: any) {
    logApiError(error, 'requestApiGet', urlConfig);
    return { en: urlConfig.en, response: { success: false } };
  }
}

export async function requestApiPost<T>(
  urlConfig: IBaseUrl,
  requestData = {},
  requestHeaders = {},
): Promise<RootApiResponseWithData<T>> {

  try {
    const res = await axiosInstance.post(
      urlConfig.url,
      requestData,
      requestHeaders,
    );
    const responseData = res?.data;

    return responseData;
  } catch (error: any) {
    logApiError(error, 'requestApiPost', urlConfig);
    return { en: urlConfig.en, response: { success: false } };
  }
}

export async function requestApiPostTwo<T>(
  urlConfig: IBaseUrl,
  requestData = {},
  requestHeaders = {},
): Promise<RootApiResponseExtends<T>> {

  try {
    // Use staging API URL for auth endpoints, fallback to default baseURL
    const isAuthEndpoint = urlConfig.url.includes('/auth/') || urlConfig.url.includes('/user/requestSignupOtp');
    let baseURL = isAuthEndpoint 
      ? (process.env.REACT_APP_API_AUTH || process.env.REACT_APP_API_SIGNUP || axiosInstance.defaults.baseURL)
      : axiosInstance.defaults.baseURL;
    
    // Remove trailing slash from baseURL to prevent double slashes
    if (baseURL) {
      baseURL = baseURL.replace(/\/$/, '');
    }
    
    // Construct full URL - handle cases where urlConfig.url might start with / or be a full URL
    let fullUrl: string;
    if (urlConfig.url.startsWith('http')) {
      fullUrl = urlConfig.url;
    } else {
      const apiPath = urlConfig.url.startsWith('/') ? urlConfig.url : `/${urlConfig.url}`;
      fullUrl = `${baseURL}${apiPath}`;
    }

    // Extract headers from axiosInstance defaults (common headers)
    const defaultHeaders = axiosInstance.defaults.headers.common || {};
    const allHeaders = {
      ...defaultHeaders,
      // 'x-zupee-env': 'cse',
      'x-zupee-source': 'web',
      // "z-stage-env-id":"stv-prya80a3bac",
      ...requestHeaders,
    };

    // Create sanitized request data for logging (hide sensitive info like OTP)
    const sanitizedRequestData = { ...requestData };
    if ('otp' in sanitizedRequestData && sanitizedRequestData.otp) {
      sanitizedRequestData.otp = '***';
    }
    
    // Request details logged internally (removed console logs)

    const res = await axios.post(
      fullUrl,
      requestData,
      {
        headers: allHeaders,
      }
    );
    const responseData = res?.data;
 
    return responseData;
  } catch (error: any) {
    console.error('Auth API Error:', {
      url: urlConfig.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    
    logApiError(error, 'requestApiPostTwo', urlConfig);
    return {
      en: urlConfig.en,
      response: { success: false, error: error.message || 'An error occurred', } as unknown as Partial<T>,
    } as RootApiResponseExtends<T>;
  }
}

function logApiError(error: Error | AxiosError, functionName: string, urlConfig: IBaseUrl) {
  if (
    !(
      error?.message === NETWORK_ERROR ||
      error?.message?.includes(TIME_OUT_EXCEED)
    )
  ) {
   
  } else if (axios.isAxiosError(error)) {
    
  } else if (isError(error)) {
   
  }
}